export const STRIKE_FEE_RATE = 0.0099;

export const toUTCDate = (s) => {
  if (!s) return new Date(NaN);

  if (s instanceof Date) return s;

  const str = String(s);

  if (/[zZ]|[+\-]\d{2}:\d{2}$/.test(str)) {
    return new Date(str);
  }

  return new Date(str + "Z");
};

export const fmtPct = (n, digits = 2) => {
  const v = Number(n);

  if (!Number.isFinite(v)) {
    return "0.00%";
  }

  return `${v.toFixed(digits)}%`;
};

export const pickBuyPrice = (t) => {
  const btcAmt = parseFloat(
    t?.btc_amt || 0
  );

  const grossUSD = parseFloat(
    t?.usd_cost || 0
  );

  const feeUSD = parseFloat(
    t?.fee || 0
  );

  const satstackerNetUSD = Math.max(
    grossUSD - feeUSD,
    0
  );

  const netUSD =
    satstackerNetUSD *
    (1 - STRIKE_FEE_RATE);

  const strikePx = parseFloat(
    t?.strike_exec_price
  );

  if (
    Number.isFinite(strikePx) &&
    strikePx > 0
  ) {
    return strikePx;
  }

  const pxAtExec = parseFloat(
    t?.price_at_exec
  );

  if (
    Number.isFinite(pxAtExec) &&
    pxAtExec > 0
  ) {
    return pxAtExec;
  }

  return (
    btcAmt > 0 &&
    netUSD > 0
  )
    ? netUSD / btcAmt
    : NaN;
};

/**
 * Entry context only.
 *
 * This compares an individual Smart Timing execution
 * with the start of its buy window.
 *
 * It is useful for saying:
 * "Smart Timing found an entry X% below the start
 * of the window."
 *
 * It is NOT Smart Timing performance vs scheduled DCA.
 * Final performance is only available after the window
 * closes and comes from:
 *
 * GET /smart-timing/performance
 */
export const calcEntryVsWindowOpen = (t) => {
  if (!t?.smart_timing) {
    return null;
  }

  const windowOpenPrice = parseFloat(
    t.window_open_price
  );

  const executionPrice =
    pickBuyPrice(t);

  if (
    !Number.isFinite(windowOpenPrice) ||
    windowOpenPrice <= 0 ||
    !Number.isFinite(executionPrice) ||
    executionPrice <= 0
  ) {
    return null;
  }

  const entryPct =
    (
      (
        windowOpenPrice -
        executionPrice
      ) /
      windowOpenPrice
    ) * 100;

  return {
    entryPct,
    windowOpenPrice,
    executionPrice,
  };
};

/**
 * @deprecated
 *
 * Legacy window-open comparison retained temporarily
 * so an older component import cannot crash during rollout.
 *
 * Do NOT use this for customer-facing Smart Timing
 * performance.
 *
 * Use GET /smart-timing/performance instead.
 */
export const calcExtraSats = (t) => {
  if (!t?.smart_timing) {
    return null;
  }

  const btcAmt = parseFloat(
    t.btc_amt || 0
  );

  const actualSats =
    Number.isFinite(btcAmt) &&
    btcAmt > 0
      ? btcAmt * 1e8
      : NaN;

  const grossUSD = parseFloat(
    t.usd_cost || 0
  );

  const feeUSD = parseFloat(
    t.fee || 0
  );

  const satstackerNetUSD =
    Math.max(
      grossUSD - feeUSD,
      0
    );

  const netUSD =
    satstackerNetUSD *
    (1 - STRIKE_FEE_RATE);

  const windowOpenPrice =
    parseFloat(
      t.window_open_price
    );

  if (
    !Number.isFinite(actualSats) ||
    actualSats <= 0
  ) {
    return null;
  }

  if (
    !Number.isFinite(netUSD) ||
    netUSD <= 0
  ) {
    return null;
  }

  if (
    !Number.isFinite(
      windowOpenPrice
    ) ||
    windowOpenPrice <= 0
  ) {
    return null;
  }

  const baselineSats =
    (
      netUSD /
      windowOpenPrice
    ) * 1e8;

  if (
    !Number.isFinite(
      baselineSats
    ) ||
    baselineSats <= 0
  ) {
    return null;
  }

  const entry =
    calcEntryVsWindowOpen(t);

  return {
    extraSats:
      actualSats -
      baselineSats,

    extraPct:
      entry?.entryPct ??
      NaN,

    baselineSats,
    actualSats,

    legacyWindowOpenComparison: true,
  };
};

/**
 * @deprecated
 *
 * Legacy aggregate retained only for rollout
 * compatibility.
 *
 * The canonical signed Smart Timing result is
 * the backend completed-window response from:
 *
 * GET /smart-timing/performance
 */
export const calcSmartTimingImpact = (
  trades = []
) => {
  let smartTimingTrades = 0;
  let totalBTC = 0;

  let costBasisUSD = 0;
  let windowValueUSD = 0;

  let totalBaselineSats = 0;
  let totalActualSats = 0;
  let totalExtraSats = 0;

  let bestTradePct = null;
  let positiveWinCount = 0;

  for (const t of trades || []) {
    if (!t?.smart_timing) {
      continue;
    }

    const btcAmt = parseFloat(
      t.btc_amt || 0
    );

    if (
      !Number.isFinite(btcAmt) ||
      btcAmt <= 0
    ) {
      continue;
    }

    const buyPx =
      pickBuyPrice(t);

    const winOpenPx =
      parseFloat(
        t.window_open_price
      );

    const legacy =
      calcExtraSats(t);

    if (
      !Number.isFinite(buyPx) ||
      buyPx <= 0 ||
      !Number.isFinite(
        winOpenPx
      ) ||
      winOpenPx <= 0 ||
      !legacy
    ) {
      continue;
    }

    smartTimingTrades += 1;
    totalBTC += btcAmt;

    costBasisUSD +=
      btcAmt * buyPx;

    windowValueUSD +=
      btcAmt * winOpenPx;

    totalBaselineSats +=
      legacy.baselineSats;

    totalActualSats +=
      legacy.actualSats;

    totalExtraSats +=
      legacy.extraSats;

    if (
      Number.isFinite(
        legacy.extraPct
      )
    ) {
      if (
        legacy.extraPct > 0
      ) {
        positiveWinCount += 1;
      }

      bestTradePct =
        bestTradePct == null
          ? legacy.extraPct
          : Math.max(
              bestTradePct,
              legacy.extraPct
            );
    }
  }

  const smartTimingGainPct =
    windowValueUSD > 0
      ? (
          (
            windowValueUSD -
            costBasisUSD
          ) /
          windowValueUSD
        ) * 100
      : 0;

  return {
    smartTimingTrades,
    positiveWinCount,

    totalBTC,

    costBasisUSD,
    windowValueUSD,

    totalBaselineSats,
    totalActualSats,
    totalExtraSats,

    smartTimingGainPct,
    bestTradePct,

    // Important:
    // Prevent old frontend code from treating this
    // legacy open-price calculation as canonical
    // performance.
    hasImpact: false,

    legacyWindowOpenComparison: true,
  };
};